import React from 'react';
import { CheckCircle, XCircle, AlertCircle } from 'lucide-react';

const Step4Review = ({
  formData,
  serviceCategories,
  countries
}) => {
  return (
    <div className="space-y-4 sm:space-y-6">
      <div>
        <h3 className="text-base sm:text-lg font-medium text-gray-900 mb-2 sm:mb-4">Review & Submit</h3>
        <p className="text-xs sm:text-sm text-gray-600 mb-4 sm:mb-6">
          Please review your information before submitting for verification.
        </p>
      </div>

      <div className="bg-gray-50 rounded-lg p-4 sm:p-6 space-y-4 sm:space-y-6">
        {/* Business Information */}
        <div>
          <h4 className="font-medium text-gray-900 mb-2 sm:mb-3 text-sm sm:text-base">Business Information</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-4 text-xs sm:text-sm">
            <div>
              <span className="text-gray-600">Business Name:</span>
              <p className="font-medium break-words">{formData.business_name}</p>
            </div>
            <div>
              <span className="text-gray-600">Service Category:</span>
              <p className="font-medium break-words">
                {formData.service_categories 
                  ? serviceCategories
                      .find(cat => cat.id === formData.service_categories)?.category_name || 'Unknown Category'
                  : 'None selected'
                }
              </p>
            </div>
            <div>
              <span className="text-gray-600">Years of Experience:</span>
              <p className="font-medium">{formData.years_experience}</p>
            </div>
            <div>
              <span className="text-gray-600">Team Size:</span>
              <p className="font-medium">{formData.team_size || 'Not specified'}</p>
            </div>
            <div className="md:col-span-2">
              <span className="text-gray-600">Available Days:</span>
              <div className="mt-1">
                <div className="flex flex-wrap gap-1.5 sm:gap-2">
                  {Object.entries(formData.business_hours).map(([day, dayData]) => (
                    <span 
                      key={day} 
                      className={`inline-flex items-center px-2 sm:px-3 py-1 rounded-full text-xs font-medium ${
                        dayData.available 
                          ? 'bg-green-100 text-green-800' 
                          : 'bg-gray-100 text-gray-600'
                      }`}
                    >
                      {dayData.available ? '✓' : '✗'} {day.charAt(0).toUpperCase() + day.slice(1)}
                    </span>
                  ))}
                </div>
              </div>
            </div>
            <div className="md:col-span-2">
              <span className="text-gray-600">Description:</span>
              <p className="font-medium break-words">{formData.business_description}</p>
            </div>
            <div className="md:col-span-2">
              <span className="text-gray-600">Specialties:</span>
              <div className="flex flex-wrap gap-1 mt-1">
                {formData.specialties.map(specialty => (
                  <span key={specialty} className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-primary-100 text-primary-800">
                    {specialty}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Contact Information */}
        <div>
          <h4 className="font-medium text-gray-900 mb-2 sm:mb-3 text-sm sm:text-base">Contact Information</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-4 text-xs sm:text-sm">
            <div>
              <span className="text-gray-600">Phone:</span>
              <p className="font-medium break-words">{formData.business_phone}</p>
            </div>
            <div>
              <span className="text-gray-600">Email:</span>
              <p className="font-medium break-words">{formData.business_email}</p>
            </div>
            <div className="md:col-span-2">
              <span className="text-gray-600">Website:</span>
              <p className="font-medium break-words">{formData.website || 'Not provided'}</p>
            </div>
            <div className="md:col-span-2">
              <span className="text-gray-600">Social Media:</span>
              <div className="mt-1 space-y-1">
                {Object.entries(formData.social_media).map(([platform, url]) => (
                  url && (
                    <div key={platform} className="flex flex-col sm:flex-row items-start sm:items-center gap-1 sm:gap-0">
                      <span className="text-xs sm:text-sm text-gray-500 capitalize w-20 flex-shrink-0">{platform}:</span>
                      <a 
                        href={url} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-xs sm:text-sm text-blue-600 hover:text-blue-800 break-all sm:truncate"
                      >
                        {url}
                      </a>
                    </div>
                  )
                ))}
                {Object.keys(formData.social_media).length === 0 && (
                  <p className="text-xs sm:text-sm text-gray-500">No social media provided</p>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Location Information */}
        <div>
          <h4 className="font-medium text-gray-900 mb-2 sm:mb-3 text-sm sm:text-base">Location Information</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-4 text-xs sm:text-sm">
            <div className="md:col-span-2">
              <span className="text-gray-600">Address:</span>
              <p className="font-medium break-words">{formData.address}</p>
            </div>
            <div>
              <span className="text-gray-600">City:</span>
              <p className="font-medium">{formData.city}</p>
            </div>
            <div>
              <span className="text-gray-600">State:</span>
              <p className="font-medium">{formData.state}</p>
            </div>
            <div>
              <span className="text-gray-600">ZIP Code:</span>
              <p className="font-medium">{formData.zip_code}</p>
            </div>
            <div>
              <span className="text-gray-600">Country:</span>
              <p className="font-medium">
                {formData.country && countries.length > 0
                  ? (() => {
                      const selectedCountry = countries.find(country => country.code === formData.country);
                      return selectedCountry ? `${selectedCountry.emoji} ${selectedCountry.name}` : formData.country;
                    })()
                  : 'Not selected'
                }
              </p>
            </div>
          </div>
        </div>

        {/* Documents */}
        <div>
          <h4 className="font-medium text-gray-900 mb-2 sm:mb-3 text-sm sm:text-base">Documents</h4>
          <div className="space-y-1.5 sm:space-y-2 text-xs sm:text-sm">
            <div className="flex items-center">
              {formData.business_license?.name ? (
                <CheckCircle className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-green-500 mr-2 flex-shrink-0" />
              ) : (
                <XCircle className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-red-500 mr-2 flex-shrink-0" />
              )}
              <span className="break-words">Business License: {formData.business_license?.name || 'Not uploaded'}</span>
            </div>
            <div className="flex items-center">
              {formData.insurance_certificate?.name ? (
                <CheckCircle className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-green-500 mr-2 flex-shrink-0" />
              ) : (
                <XCircle className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-red-500 mr-2 flex-shrink-0" />
              )}
              <span className="break-words">Insurance Certificate: {formData.insurance_certificate?.name || 'Not uploaded'}</span>
            </div>
            <div className="flex items-center">
              {formData.id_document?.name ? (
                <CheckCircle className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-green-500 mr-2 flex-shrink-0" />
              ) : (
                <XCircle className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-red-500 mr-2 flex-shrink-0" />
              )}
              <span className="break-words">ID Document: {formData.id_document?.name || 'Not uploaded'}</span>
            </div>
          </div>
        </div>
      </div>

      <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 sm:p-4">
        <div className="flex">
          <AlertCircle className="h-4 w-4 sm:h-5 sm:w-5 text-blue-400 mr-2 sm:mr-3 mt-0.5 flex-shrink-0" />
          <div>
            <h4 className="text-xs sm:text-sm font-medium text-blue-900">Verification Process</h4>
            <p className="text-xs sm:text-sm text-blue-700 mt-1">
              After submission, our team will review your information and documents. 
              This process typically takes 1-3 business days. You'll receive an email 
              notification once your verification is complete.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Step4Review;


